import { showAudioLevels, hostLink, renderQR, copyWithFeedback, setPressed, isIos, getStoredJSON, setStoredJSON, getClientIdentity, rememberLastHost, openToast, closeToast, withViewTransition } from './utils.js';
import { track, notePeerRecovery, EVENTS } from './analytics.js';
import { describeConnection, isRelayed } from './webrtc-stats.js';
import { attachIceRecovery } from './ice-restart.js';
import { enablePush, disablePush } from './push.js';
import { playConnectionLost, playConnectionRestored, playBatteryWarning, playDisconnectedAlarm, initCue } from './alert-cue.js';
import { t } from './i18n.js';
import jsQR from 'jsqr';

const $talkBack = document.getElementById('talk-back');
const $fitToggle = document.getElementById('fit-toggle');
const $muteToggle = document.getElementById('mute-toggle');
const $alertsToggle = document.getElementById('alerts-toggle');
const $melodyToggle = document.getElementById('melody-toggle');
const $invite = document.getElementById('invite');
const $inviteOverlay = document.getElementById('invite-overlay');
const $inviteCopy = document.getElementById('invite-copy');
const $othersBadge = document.getElementById('others-badge');
const $clientStatus = document.getElementById('client-status');
const $soundIndicator = document.getElementById('sound-indicator');
const $batteryIndicator = document.getElementById('battery-indicator');
const $scanVideo = document.getElementById('scan-video');
const $startScan = document.getElementById('start-scan');

const ALERT_KEY = 'peekababy:cry-alert';
// Remembers the parent's framing (fit mode + zoom + pan) per monitor so the same
// picture is restored after a reconnect or a full reload, instead of snapping
// back to the default 1× cover view.
const VIEW_KEY = 'peekababy:view';
// Normalised (0..1) loudness bands for the passive "time since sound" readout.
const SOUND_ON = 0.15;

// Leading glyphs for instructional toasts (e.g. "Add the app to your Home
// Screen"), so the parent recognises the platform action at a glance. Static,
// trusted markup — never interpolate user text into these. `ios` is the Share
// sheet glyph (tap Share → Add to Home Screen); `android` is the app-to-phone
// "install / add to Home screen" glyph.
const HINT_ICONS = {
  ios: '<svg class="toast-hint-ico" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 15V4"/><path d="m8 8 4-4 4 4"/><path d="M7 12H5.5A1.5 1.5 0 0 0 4 13.5v5A1.5 1.5 0 0 0 5.5 20h13a1.5 1.5 0 0 0 1.5-1.5v-5A1.5 1.5 0 0 0 18.5 12H17"/></svg>',
  android: '<svg class="toast-hint-ico" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="6" y="3" width="12" height="18" rx="2.5"/><path d="M12 8.5v5M9.5 11h5"/></svg>',
};

// A dropped link usually self-heals within seconds (a Wi-Fi<->mobile handoff or
// an ICE blip), so we don't nag on every blip. If it doesn't recover we escalate
// in two stages, since a monitor that stays unreachable means no eyes on the
// baby:
//   1. After OUTAGE_SOFT_DELAY (20s) — a light, repeating heads-up (soft cue +
//      short buzz) every OUTAGE_SOFT_INTERVAL, so the parent notices without
//      being alarmed for what may still be a passing blip.
//   2. After OUTAGE_ALARM_DELAY (60s) — drop the light cue and escalate to a
//      loud, clear alarm (bright tone + strong buzz) every OUTAGE_BEEP_INTERVAL
//      until the feed recovers.
// Foreground-only: Web Audio and timers are suspended when the tab is
// backgrounded, so this covers "watching on a nearby phone"; locked-phone
// coverage needs Web Push.
const OUTAGE_SOFT_DELAY = 20 * 1000;
const OUTAGE_SOFT_INTERVAL = 20 * 1000;
const OUTAGE_ALARM_DELAY = 60 * 1000;
const OUTAGE_BEEP_INTERVAL = 10 * 1000;
// A single short buzz for the light stage — a gentle tap on the shoulder.
const OUTAGE_SOFT_VIBRATE = [180];
// Long, insistent buzz pattern (three strong pulses) for the loud stage, so a
// sustained outage is felt clearly even with the phone in a pocket or face-down
// on a nightstand.
const OUTAGE_VIBRATE = [500, 150, 500, 150, 500];

// After this many dial attempts that never produced a live feed, swap the
// endless "Reconnecting…" for an honest "can't reach this monitor" hint — a feed
// that never once connected is almost always a stale/expired link, not a blip.
// We keep retrying in the background so a monitor that comes up late still
// connects and clears the hint.
const PRELIVE_FAIL_LIMIT = 4;

// How often to check that the metadata channel is still up and reopen it if not.
// Cosmetic data (count) plus the battery warning and live-frame heartbeat ride
// it, so a few seconds of lag on recovery is fine; the poll stays cheap.
const DC_SUPERVISOR_MS = 3 * 1000;

// Decode-stall tolerance, in 2s liveness ticks. Normally ~8s of no new decoded
// frames means the feed is dead. But a capped encoder on a still, dark nursery
// legitimately delivers very few frames, so when the monitor's heartbeat proves
// the camera is still producing frames we wait far longer before re-dialing.
const STALE_TICKS_NORMAL = 4;
const STALE_TICKS_SOURCE_LIVE = 15;

// Parent focus (immersive) mode: after this long with no interaction on a live
// feed, fade the chrome away so only the picture and the "Live" pill remain.
const FOCUS_IDLE_DELAY = 20 * 1000;
// Change in acceleration magnitude (m/s²) that counts as a deliberate move —
// picking the phone up or nudging it — versus resting jitter. Comparing the
// delta works whether the browser reports gravity-free `acceleration` (rests
// near 0) or `accelerationIncludingGravity` (rests near 9.8).
const MOTION_WAKE_DELTA = 2.2;

export default class Client {
  constructor(peer, hostId) {
    this.peer = peer;
    this.hostId = hostId || null;
    // Stable per-device identity sent to the host so it can recognize this
    // parent across reconnects/reloads (PeerJS ids are per-load, so they can't
    // be used for this). Recognition only — never treated as authentication.
    this.identity = getClientIdentity();
    this.audioStream = null;
    this.currentCall = null;
    // Per-call ICE restart controller (see ice-restart.js); recreated each dial.
    this.iceRecovery = null;
    this.dc = null;
    // The metadata channel rides its own RTCPeerConnection (peer.connect), so it
    // can drop while the media call stays healthy — and nothing would reopen it,
    // since openDataChannel only ran on a fresh 'stream' event. This timer polls
    // for that gap and re-establishes the channel, keeping the viewer count,
    // remote lullaby, battery warnings and the live-frame heartbeat alive.
    this.dcSupervisor = null;
    this.reconnectDelay = 1000;
    this.retryTimer = null;
    this.watchdog = null;
    this.disconnectGrace = null;
    this.resumeGuard = null;
    this.connectionLive = false;
    // Whether we've ever had a live connection. Used so the "reconnected" cue
    // only plays on an actual recovery, never on the very first connect.
    this.everLive = false;
    // Consecutive dial attempts that never reached a live feed (drives the
    // "monitor not found" hint). Reset the moment we ever go live.
    this.preLiveFailures = 0;
    // The host id our push subscription is currently registered under, so we can
    // re-subscribe when it changes (scan flow sets hostId late; a host reload can
    // change it) without redundant /subscribe calls on every reconnect.
    this._subscribedHostId = null;

    this.lastSound = null; // { at, band }
    this.soundPeak = 0;

    // Host-side "live video" heartbeat. The monitor broadcasts a counter of real
    // camera frames (see host startLiveHeartbeat); if those heartbeats keep
    // arriving but the counter stops advancing, the camera at the monitor has
    // frozen even though bytes and decoded frames may still be flowing — the one
    // case our own inbound stats can't catch. `hostHeartbeatAt` gates the check
    // so monitors that don't send it (older builds) are never wrongly flagged.
    this.hostFrameSeq = null;
    this.hostFrameChangedAt = 0;
    this.hostHeartbeatAt = 0;
    // True while we're showing the dedicated "monitor camera frozen" state: the
    // link is healthy and media may still be decoding, but the monitor's camera
    // stopped producing new frames, so reconnecting wouldn't help.
    this.cameraFrozen = false;

    // Whether the baby monitor is currently playing the lullaby. Kept in sync
    // with the host over the data channel; the parent can also toggle it.
    this.melodyOn = false;

    // Whether the monitor reported a critically low battery. While true we show
    // a subtle pill and repeat a soft warning tone (batteryTimer).
    this.batteryLow = false;
    this.batteryTimer = null;

    // Sustained-outage escalation. outageSoftTimer waits 20s after a drop, then
    // outageSoftTimer2 repeats a light heads-up; outageArmTimer waits 60s, then
    // outageBeepTimer repeats the loud alarm until the feed is back.
    this.outageSoftTimer = null;
    this.outageSoftTimer2 = null;
    this.outageArmTimer = null;
    this.outageBeepTimer = null;

    // Video framing (parent). `fitMode` is the base fit: 'cover' fills the
    // screen (cropping when host/viewer aspect ratios differ), 'contain'
    // letterboxes the whole frame. On top of that the parent can pinch to zoom
    // (`zoom` ≥ 1) and drag to pan (`tx`/`ty`, in screen px). Both work in
    // either fit mode: applyFraming() renders with object-fit:contain and folds
    // the fill-crop and the user's zoom into a single transform, clamped so the
    // picture can never be dragged into a black gap.
    this.fitMode = 'cover';
    this.zoom = 1;
    this.tx = 0;
    this.ty = 0;
    this.maxZoom = 6;
    // 'land' | 'port' | 'square' of the last stream, so we only reset the view
    // when the feed's orientation actually flips (not on every reconnect).
    this.lastOrient = null;
    // Whether we've restored the remembered framing yet this session (done once,
    // as soon as the stream's real dimensions are known — see onVideoMetadata).
    this._viewRestored = false;

    this.bindMute();
    this.bindTalkBack();
    this.bindAlerts();
    this.bindInvite();
    this.bindMelody();
    this.bindFraming();
    this.initFocusMode();
    this.startSoundClock();
    // Prime the connection-cue audio so the parent's first tap unlocks it,
    // ensuring the "lost"/"restored" tones can play on the first real drop.
    initCue();

    document.addEventListener('visibilitychange', () => this.resumeIfNeeded());
    ['pageshow', 'focus', 'online'].forEach((evt) =>
      window.addEventListener(evt, () => this.resumeIfNeeded())
    );
    // A network hand-off (e.g. Wi-Fi -> mobile) kills the current path instantly.
    // Surface "Reconnecting…" straight away instead of waiting up to ~12s for ICE
    // to notice; the matching 'online' event then drives the actual re-dial.
    window.addEventListener('offline', () => this.onNetworkLost());

    // Always wire the scanner button so "Open camera" works whenever the scan
    // screen is shown — the initial no-host flow AND the fallback we surface when
    // a remembered/deep-linked monitor can't be reached (see revealScanner).
    if ($startScan) $startScan.addEventListener('click', () => this.startScan());

    if (this.hostId) {
      // Have a target (deep link, QR, or a remembered monitor): dial it. The
      // parent taps "Open camera" only if we later fall back to the scanner.
      this.beginConnection();
    } else {
      this.revealScanner();
    }
  }

  // Show the QR scanner and make its "Open camera" button tappable. Used for the
  // initial no-host flow and as a fallback when a remembered/deep-linked monitor
  // can't be reached, so the parent always has a way to re-scan or go Back.
  revealScanner() {
    document.body.classList.add('need-scan');
    // app.js may have disabled the button while it revealed the scanner early for
    // the entry morph; it's safe to enable now that the handler is attached.
    if ($startScan) $startScan.disabled = false;
  }

  // ---- QR scanning (parent) ------------------------------------------------
  async startScan() {
    if (this.scanning) return;
    try {
      this.scanStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' },
        audio: false,
      });
    } catch (err) {
      this.setScanHint(t('scan.cameraNeeded'));
      console.warn('[CLIENT] scan camera failed', err && err.name);
      return;
    }
    this.scanning = true;
    document.body.classList.add('scanning');
    if ($startScan) $startScan.textContent = t('scan.scanning');
    $scanVideo.srcObject = this.scanStream;
    $scanVideo.setAttribute('playsinline', '');
    try { await $scanVideo.play(); } catch (e) { /* will still produce frames */ }

    const detector = ('BarcodeDetector' in window)
      ? new window.BarcodeDetector({ formats: ['qr_code'] })
      : null;
    let nativeDetector = detector;
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d', { willReadFrequently: true });

    const tick = async () => {
      if (!this.scanning) return;
      let text = null;
      if ($scanVideo.readyState >= 2 && $scanVideo.videoWidth) {
        if (nativeDetector) {
          try {
            const codes = await nativeDetector.detect($scanVideo);
            if (codes && codes.length) text = codes[0].rawValue;
          } catch (e) {
            nativeDetector = null; // fall back to jsQR
          }
        }
        if (!text) {
          canvas.width = $scanVideo.videoWidth;
          canvas.height = $scanVideo.videoHeight;
          ctx.drawImage($scanVideo, 0, 0, canvas.width, canvas.height);
          const img = ctx.getImageData(0, 0, canvas.width, canvas.height);
          const res = jsQR(img.data, img.width, img.height, { inversionAttempts: 'dontInvert' });
          if (res && res.data) text = res.data;
        }
      }
      // A code that isn't one of our links is ignored and scanning continues.
      const id = text ? this.parseHostId(text) : null;
      if (id) return this.onScanResult(id);
      this.scanRaf = requestAnimationFrame(tick);
    };
    this.scanRaf = requestAnimationFrame(tick);
  }

  onScanResult(id) {
    this.stopScan();
    track(EVENTS.QR_SCANNED);
    // Scanning a different monitor: drop any channel still pointed at the old one
    // so the supervisor reopens it against the new host rather than leaving a dead
    // reference in place.
    if (this.dc && id !== this.hostId) {
      try { this.dc.close(); } catch (e) { /* already closed */ }
      this.dc = null;
    }
    this.hostId = id;
    // Cancel any retry still pending from a prior target (the scanner can be
    // shown as a fallback while a failed connect keeps retrying in the
    // background). Without this, that timer would fire placeCall() alongside the
    // one below and dial the freshly scanned monitor twice, leaking a duplicate
    // call. beginConnection -> placeCall re-arms the retry as needed.
    clearTimeout(this.retryTimer);
    this.retryTimer = null;
    // Cross-fade the scanner out to the connecting state; the logo fades with it
    // (no matching element on the live view, so it eases out cleanly).
    withViewTransition(() => document.body.classList.remove('need-scan'));
    this.beginConnection();
  }

  parseHostId(text) {
    if (!text) return null;
    const hashIdx = text.indexOf('#');
    if (hashIdx !== -1) {
      const id = text.slice(hashIdx + 1).trim();
      return id || null;
    }
    // Bare id (no URL) — accept a plausible peer id token.
    if (/^[A-Za-z0-9_-]{6,}$/.test(text.trim())) return text.trim();
    return null;
  }

  stopScan() {
    this.scanning = false;
    document.body.classList.remove('scanning');
    if (this.scanRaf) cancelAnimationFrame(this.scanRaf);
    this.scanRaf = 0;
    if (this.scanStream) {
      this.scanStream.getTracks().forEach((t) => t.stop());
      this.scanStream = null;
    }
    try { $scanVideo.srcObject = null; } catch (e) { /* ignore */ }
  }

  setScanHint(text) {
    const hint = document.getElementById('scan-hint');
    if (hint) hint.textContent = text;
  }

  // ---- Connection ----------------------------------------------------------
  beginConnection() {
    this.startDataChannelSupervisor();
    this.setStatus('connecting', t('status.connecting'));
    // Push-to-talk needs the mic, but a denied mic must not stop the parent from
    // watching — fall back to an empty stream so video still negotiates.
    navigator.mediaDevices.getUserMedia({ video: false, audio: true })
      .then((audioStream) => {
        this.audioStream = audioStream;
        this.setMicEnabled(false);
        this.placeCall();
      })
      .catch((err) => {
        track(EVENTS.MIC_ERROR, { name: err && err.name });
        console.error('[CLIENT] Could not access microphone', err);
        this.audioStream = new MediaStream();
        this.placeCall();
      });
  }

  placeCall() {
    clearTimeout(this.retryTimer); this.retryTimer = null;
    clearTimeout(this.watchdog);
    clearTimeout(this.disconnectGrace);

    // When this dial started, so the first 'stream' can report time-to-connect.
    this._dialStartedAt = Date.now();
    const call = this.peer.call(this.hostId, this.audioStream, {
      metadata: { clientId: this.identity.id, name: this.identity.name },
      constraints: { offerToReceiveVideo: true, offerToReceiveAudio: true },
    });
    if (!call) return this.scheduleReconnect();
    this.currentCall = call;

    // Try an in-place ICE restart before a full re-dial: on a blip this keeps the
    // picture up and skips a fresh DTLS handshake + getUserMedia. onGiveUp falls
    // back to the existing re-dial; onRecovered re-checks the (separate) data
    // channel, since no new 'stream' fires after a restart.
    this.iceRecovery = attachIceRecovery(call.peerConnection, {
      peer: this.peer,
      remoteId: this.hostId,
      connectionId: call.connectionId,
      role: 'offerer',
      onGiveUp: () => this.scheduleReconnect(),
      onRecovered: () => { this.reconnectDelay = 1000; this.ensureDataChannel(); },
    });

    this.watchdog = setTimeout(() => this.scheduleReconnect(), 12000);

    call.on('stream', (remoteStream) => {
      clearTimeout(this.watchdog);
      this.reconnectDelay = 1000;
      // Connected: clear any "not found" hint (and the scanner fallback) and its
      // failure streak.
      this.preLiveFailures = 0;
      document.body.classList.remove('not-found', 'need-scan');
      // Remember this monitor so a parent whose app was fully closed can rejoin
      // on reopen without going back to the room to re-scan (see app.js resume).
      rememberLastHost(this.hostId);
      // Make sure background alerts are subscribed under the host we actually
      // reached (fixes the scan flow, where hostId is set after init, and a host
      // that reclaimed/changed its id).
      this.maybeRefreshPush();
      // Feed is live: re-arm peer-error tracking so any *future* drop is logged
      // as a fresh outage instead of being suppressed as the last one's tail.
      notePeerRecovery();
      $video.srcObject = remoteStream;
      showAudioLevels(remoteStream, (level) => this.onSoundLevel(level));
      this.playRemoteStream();
      // Re-assert the current framing on the new stream. The zoom/pan is kept
      // here; onVideoMetadata() only re-centres if the orientation flipped.
      this.applyFraming();
      // Ease the live picture in the first time it lands rather than snapping
      // it on; reconnections already have the stream up, so this is a no-op then.
      if (!document.body.classList.contains('has-stream')) {
        withViewTransition(() => document.body.classList.add('has-stream'));
      } else {
        document.body.classList.add('has-stream');
      }
      this.setStatus('live', t('status.live'));
      // A fresh live picture: reveal the controls and start the idle clock that
      // fades to the picture-only focus view (see initFocusMode).
      this.wakeControls();
      // Watch the actual media flow, not just ICE state: a WebRTC link can stay
      // "connected" while the picture silently freezes. This keeps the "Live"
      // dot honest and re-dials on a truly stalled feed.
      this.startLivenessWatch(call.peerConnection);
      this.openDataChannel();
      // Feed is back: silence any sustained-outage alarm regardless of whether
      // this is the first connect or a recovery.
      this.stopOutageAlarm();
      if (!this.connectionLive) {
        this.connectionLive = true;
        // Soft "we're back" cue on recovery only — the first connect is silent.
        if (this.everLive) playConnectionRestored();
        this.everLive = true;
        track(EVENTS.STREAM_ESTABLISHED);
      }
      // Report how this connection was actually established (candidate types,
      // protocol, RTT) and how long it took. Deferred a beat so the selected
      // candidate pair is populated in getStats.
      this.reportConnection(call.peerConnection, this._dialStartedAt);
    });

    // A genuine close (remote hang-up / explicit teardown) still re-dials; but a
    // close racing an in-flight ICE restart must not, or we'd abandon the recovery.
    call.on('close', () => {
      if (this.iceRecovery && this.iceRecovery.isRecovering()) return;
      this.scheduleReconnect();
    });

    const pc = call.peerConnection;
    if (pc) {
      pc.addEventListener('connectionstatechange', () => {
        const state = pc.connectionState;
        const recover = () => (this.iceRecovery ? this.iceRecovery.recover('conn-' + state) : this.scheduleReconnect());
        if (state === 'failed') {
          recover();
        } else if (state === 'disconnected') {
          clearTimeout(this.disconnectGrace);
          this.disconnectGrace = setTimeout(() => {
            if (['disconnected', 'failed'].includes(pc.connectionState)) recover();
          }, 4000);
        } else if (state === 'connected') {
          clearTimeout(this.disconnectGrace);
          this.reconnectDelay = 1000;
        }
      });
    }
  }

  teardownCall() {
    clearTimeout(this.watchdog);
    clearTimeout(this.disconnectGrace);
    this.stopLivenessWatch();
    // Drop the battery warning on any teardown; the host re-sends its state on
    // the next data channel, re-arming it if the battery is still low.
    this.clearBatteryWarning();
    if (this.iceRecovery) {
      this.iceRecovery.destroy();
      this.iceRecovery = null;
    }
    if (this.currentCall) {
      try { this.currentCall.close(); } catch (e) { /* already closed */ }
      this.currentCall = null;
    }
  }

  // ---- Liveness watchdog ---------------------------------------------------
  // Poll the peer connection's inbound stats for proof the *picture* is moving,
  // not just that some bytes are arriving. We key off decoded video frames: as
  // long as framesDecoded keeps climbing the decoder is producing pictures, so
  // the "Live" dot is truthful even for a perfectly still, sleeping baby. If it
  // stops for a stretch while the link still claims to be connected, the video
  // has frozen — drop to the reconnecting state and re-dial. (Summing raw bytes
  // wasn't enough: audio keeps bytes flowing, so a frozen picture could have
  // masqueraded as live.)
  startLivenessWatch(pc) {
    this.stopLivenessWatch();
    if (!pc || typeof pc.getStats !== 'function') return;
    this._livePc = pc;
    this._lastLiveSignal = null;
    this._staleTicks = 0;
    // Fresh call: forget the previous connection's heartbeat so a stale counter
    // can't trip the source-freeze check before new heartbeats arrive.
    this.hostFrameSeq = null;
    this.hostFrameChangedAt = 0;
    this.hostHeartbeatAt = 0;
    this.clearCameraFrozen();
    this.livenessTimer = setInterval(() => this.checkLiveness(), 2000);
  }

  // Record a camera-frame heartbeat from the monitor. We track when a heartbeat
  // last arrived (so we only trust the check while they're actually flowing) and
  // when the counter last changed (a frozen camera stops advancing it).
  noteHostFrame(seq) {
    if (seq == null) return;
    const now = Date.now();
    this.hostHeartbeatAt = now;
    if (seq !== this.hostFrameSeq) {
      this.hostFrameSeq = seq;
      this.hostFrameChangedAt = now;
      // New frames again: the camera resumed, so leave the frozen state.
      if (this.cameraFrozen) this.exitCameraFrozen();
    }
  }

  // Enter the dedicated "monitor camera frozen" state: the link is healthy so we
  // keep it (a re-dial can't un-freeze a camera), but the picture is stuck, so
  // we say so plainly and drop the green "Live" pill to an honest warning.
  enterCameraFrozen() {
    if (this.cameraFrozen) return;
    this.cameraFrozen = true;
    document.body.classList.add('camera-frozen');
    if ($clientStatus) {
      $clientStatus.classList.remove('live');
      $clientStatus.classList.add('warn');
      const $t = $clientStatus.querySelector('.status-text');
      if ($t) $t.textContent = t('status.cameraStopped');
    }
    track(EVENTS.CAMERA_FROZEN);
    // Surface the controls (and, via canFocus, keep focus mode from hiding them
    // while this alert is up).
    this.wakeControls();
  }

  // Camera resumed while still connected: clear the state and restore Live.
  exitCameraFrozen() {
    if (!this.cameraFrozen) return;
    this.clearCameraFrozen();
    this.setStatus('live', t('status.live'));
    this.wakeControls();
  }

  // Drop the frozen flag/overlay without touching the pill — used when we're
  // transitioning into another state that owns the pill (reconnect, new call).
  clearCameraFrozen() {
    if (!this.cameraFrozen) return;
    this.cameraFrozen = false;
    document.body.classList.remove('camera-frozen');
  }

  stopLivenessWatch() {
    if (this.livenessTimer) clearInterval(this.livenessTimer);
    this.livenessTimer = null;
    this._livePc = null;
    this._lastLiveSignal = null;
    this._staleTicks = 0;
  }

  async checkLiveness() {
    const pc = this._livePc;
    if (!pc || typeof pc.getStats !== 'function') return;
    // An ICE restart briefly stops decoded frames by design; don't let the stall
    // detector tear it down before it can land (the restart has its own watchdog).
    if (this.iceRecovery && this.iceRecovery.isRecovering()) { this._staleTicks = 0; return; }
    let videoFrames = null; // decoded video frames — the true "picture is moving" signal
    let videoBytes = 0;
    let anyBytes = 0;
    try {
      const stats = await pc.getStats();
      stats.forEach((r) => {
        if (r.type !== 'inbound-rtp') return;
        if (typeof r.bytesReceived === 'number') anyBytes += r.bytesReceived;
        const kind = r.kind || r.mediaType;
        if (kind !== 'video') return;
        if (typeof r.bytesReceived === 'number') videoBytes += r.bytesReceived;
        if (typeof r.framesDecoded === 'number') videoFrames = (videoFrames || 0) + r.framesDecoded;
      });
    } catch (e) {
      return; // transient; try again next tick
    }
    // Guard against a race where the watch was torn down mid-await.
    if (pc !== this._livePc) return;

    // Prefer decoded video frames; fall back to video bytes, then to total bytes
    // only if the browser exposes neither (very old getStats) — so we degrade
    // gracefully but never silently drop back to the audio-can-fake-it behaviour
    // when a better signal is available.
    const signal = videoFrames !== null ? videoFrames
      : (videoBytes > 0 ? videoBytes : anyBytes);

    // Source-side liveness: the monitor's camera-frame heartbeat is still
    // arriving (fresh within ~6s) and its counter is still climbing. Only trusted
    // while heartbeats flow, so older monitors that don't send them are never
    // considered here.
    const now = Date.now();
    const heartbeatFresh = this.hostHeartbeatAt && (now - this.hostHeartbeatAt) < 6000;
    const sourceAdvancing = heartbeatFresh
      && this.hostFrameChangedAt
      && (now - this.hostFrameChangedAt) < 6000;

    // Decode-side staleness: our inbound video (or, failing that, any media)
    // stopped progressing while the link still claims to be connected. Allow a
    // few ticks (~8s) so a brief hiccup doesn't trigger a needless reconnect.
    // When the source is provably still producing frames, be far more patient:
    // a capped encoder on a near-static nursery legitimately sends very few
    // decoded frames, and tearing that down would loop pointless re-dials. A
    // genuinely dead media path stops the heartbeat too (separate connection) or
    // trips the ICE state handler, so the longer window is a safe backstop.
    const staleLimit = sourceAdvancing ? STALE_TICKS_SOURCE_LIVE : STALE_TICKS_NORMAL;
    let decodeStalled = false;
    if (this._lastLiveSignal === null) {
      this._lastLiveSignal = signal;
    } else if (signal > this._lastLiveSignal) {
      this._lastLiveSignal = signal;
      this._staleTicks = 0;
    } else {
      this._staleTicks += 1;
      if (this._staleTicks >= staleLimit) decodeStalled = true;
    }

    // Source-side staleness: the heartbeat is fresh but its counter has stopped
    // climbing — the camera at the monitor has frozen even though frames may
    // still be decoding here. A re-dial can't fix that, so it's handled as its
    // own state below rather than as an outage.
    const sourceStalled = heartbeatFresh
      && this.hostFrameChangedAt
      && (now - this.hostFrameChangedAt) > 8000;

    // If we're showing the frozen state but the heartbeats that justified it have
    // themselves stopped (e.g. the data channel dropped while the media call
    // stayed up), we can no longer confirm the camera is stuck. Give the benefit
    // of the doubt and fall back to the decode-based liveness rather than pinning
    // up an unverifiable overlay; a resumed heartbeat re-detects a real freeze.
    if (this.cameraFrozen && !heartbeatFresh) this.exitCameraFrozen();

    // A decode/network stall is a real outage — reconnect (and drop any frozen
    // state, since we're now genuinely offline). A source-only stall means the
    // link is fine but the monitor's camera stopped: hold the connection and
    // show the dedicated frozen state instead of looping a pointless re-dial.
    if (decodeStalled) {
      console.warn('[CLIENT] Live video stalled — reconnecting');
      this.scheduleReconnect();
    } else if (sourceStalled) {
      this.enterCameraFrozen();
    }
  }

  scheduleReconnect() {
    if (this.retryTimer) return;
    // A real outage supersedes the frozen state; the reconnecting flow owns the
    // pill/overlay from here.
    this.clearCameraFrozen();
    this.teardownCall();
    if (this.connectionLive) {
      this.connectionLive = false;
      // Soft "link dropped" cue. Guarded by connectionLive so it fires once per
      // outage, and never while first connecting.
      playConnectionLost();
      track(EVENTS.CONNECTION_LOST);
      // Arm the escalating alarm for an outage that doesn't self-heal. Started
      // here (not on every retry) since this block runs once per outage.
      this.startOutageAlarm();
    }
    // A feed that has never once gone live is very likely a stale/expired link;
    // after a few failed dials, say so plainly instead of "Reconnecting…"
    // forever. We still keep retrying, so a late-arriving host clears it.
    if (!this.everLive) this.preLiveFailures += 1;
    const notFound = !this.everLive && this.preLiveFailures >= PRELIVE_FAIL_LIMIT;
    document.body.classList.toggle('not-found', notFound);
    // A target we never reached (stale deep link, or a remembered monitor that's
    // now off): surface the scanner so the parent can re-scan or go Back, while
    // we keep retrying in the background in case the monitor comes back.
    if (notFound) this.revealScanner();
    this.setStatus('warn', t(notFound ? 'status.notFound' : 'status.reconnecting'));
    // Jitter each retry by ±25% so several parents dropped by the same event
    // (e.g. a monitor restart) don't redial in lockstep and stampede it.
    const jittered = Math.round(this.reconnectDelay * (0.75 + Math.random() * 0.5));
    console.warn(`[CLIENT] Connection lost — retrying in ${jittered}ms`);
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      this.placeCall();
    }, jittered);
    this.reconnectDelay = Math.min(this.reconnectDelay * 2, 15000);
  }

  // One-shot report of how a freshly live connection was established. Best-effort
  // and privacy-safe (see webrtc-stats.js): coarse candidate types only, so it's
  // safe under the analytics rules. The short delay lets the selected candidate
  // pair settle in getStats after the first frame.
  reportConnection(pc, startedAt) {
    if (!pc) return;
    setTimeout(async () => {
      const desc = await describeConnection(pc);
      if (!desc) return;
      track(EVENTS.ICE_CONNECTED, {
        local: desc.local,
        remote: desc.remote,
        protocol: desc.protocol,
        rtt: desc.rtt,
        relayed: isRelayed(desc),
        ms: startedAt ? Date.now() - startedAt : null,
      });
    }, 1500);
  }

  reconnectNow() {
    if (!this.audioStream) return;
    clearTimeout(this.retryTimer); this.retryTimer = null;
    this.teardownCall();
    this.reconnectDelay = 1000;
    this.placeCall();
  }

  // ---- Sustained-outage escalation (parent, foreground) --------------------
  // Two-stage escalation after a drop: a light, repeating heads-up from 20s, then
  // a loud, clear alarm from 60s, each on repeat until recovery. Foreground-only
  // by nature (Web Audio/timers pause when backgrounded); a locked phone relies
  // on Web Push instead.
  startOutageAlarm() {
    this.stopOutageAlarm();
    // Stage 1 (20s): light heads-up, repeated gently until the loud alarm takes
    // over at 60s.
    this.outageSoftTimer = setTimeout(() => {
      this.outageSoftTimer = null;
      this.outageSoftNudge();
      this.outageSoftTimer2 = setInterval(() => this.outageSoftNudge(), OUTAGE_SOFT_INTERVAL);
    }, OUTAGE_SOFT_DELAY);
    // Stage 2 (60s): stop the light cue and escalate to a loud alarm on repeat.
    this.outageArmTimer = setTimeout(() => {
      this.outageArmTimer = null;
      clearInterval(this.outageSoftTimer2);
      this.outageSoftTimer2 = null;
      track(EVENTS.OUTAGE_ALARM);
      this.outageBeep();
      this.outageBeepTimer = setInterval(() => this.outageBeep(), OUTAGE_BEEP_INTERVAL);
    }, OUTAGE_ALARM_DELAY);
  }

  // Light stage: a soft cue plus a single short buzz — noticeable, not alarming.
  outageSoftNudge() {
    playConnectionLost();
    if (navigator.vibrate) {
      try { navigator.vibrate(OUTAGE_SOFT_VIBRATE); } catch (e) { /* unsupported */ }
    }
  }

  // Loud stage: the bright alarm tone plus a strong, insistent buzz.
  outageBeep() {
    playDisconnectedAlarm();
    if (navigator.vibrate) {
      try { navigator.vibrate(OUTAGE_VIBRATE); } catch (e) { /* unsupported */ }
    }
  }

  stopOutageAlarm() {
    clearTimeout(this.outageSoftTimer);
    clearInterval(this.outageSoftTimer2);
    clearTimeout(this.outageArmTimer);
    clearInterval(this.outageBeepTimer);
    this.outageSoftTimer = null;
    this.outageSoftTimer2 = null;
    this.outageArmTimer = null;
    this.outageBeepTimer = null;
  }

  // The OS reports the current network went away (typically a Wi-Fi <-> mobile
  // handoff). If we were live, show the reconnecting state immediately rather
  // than letting the frozen picture linger until ICE times out.
  onNetworkLost() {
    if (!this.hostId || !this.everLive) return;
    this.scheduleReconnect();
  }

  resumeIfNeeded() {
    if (document.visibilityState !== 'visible') return;
    if (!this.hostId) return; // still on the scan screen
    if (this.resumeGuard) return;
    this.resumeGuard = setTimeout(() => { this.resumeGuard = null; }, 800);

    if (this.peer && this.peer.disconnected && !this.peer.destroyed) {
      try { this.peer.reconnect(); } catch (e) { /* will retry below */ }
    }

    const pc = this.currentCall && this.currentCall.peerConnection;
    const alive = pc && pc.connectionState === 'connected';

    if (!alive) {
      this.reconnectNow();
    } else {
      // Link is fine, but a channel dropped while backgrounded is exactly the
      // gap the supervisor covers — close it now rather than after the next poll.
      this.ensureDataChannel();
      if ($video && $video.paused) this.playRemoteStream();
    }
  }

  playRemoteStream() {
    const play = () => $video.play();
    play().catch(() => {
      this.setMuted(true);
      play().catch((err) => console.warn('[CLIENT] video play blocked', err && err.name));
      const unmute = () => {
        this.setMuted(false);
        if ($video.paused) play().catch(() => {});
      };
      ['click', 'touchend'].forEach((evt) =>
        document.addEventListener(evt, unmute, { once: true })
      );
    });
    this.syncMuteButton();
  }

  // ---- Status pill ---------------------------------------------------------
  setStatus(kind, text) {
    if (!$clientStatus) return;
    const waiting = kind === 'connecting' || kind === 'warn';
    document.body.classList.toggle('connecting', waiting);
    // Only raise the full-screen "Reconnecting…" overlay once we've actually had
    // a live picture. The very first connect keeps the plain "Connecting…" pill
    // so a fresh scan/deep-link doesn't get an alarming interruption screen.
    document.body.classList.toggle('reconnecting', waiting && this.everLive);
    $clientStatus.classList.toggle('live', kind === 'live');
    $clientStatus.classList.toggle('warn', kind === 'warn' || kind === 'connecting');
    const $t = $clientStatus.querySelector('.status-text');
    if ($t) $t.textContent = text;
    // Anything other than a steady live feed (connecting/reconnecting) must show
    // the full chrome, so surface the controls and stop the idle clock; a return
    // to 'live' re-arms focus from the stream handler.
    if (kind !== 'live') this.wakeControls();
  }

  // ---- Focus (immersive) mode ----------------------------------------------
  // On a live feed the chrome (toolbar, push-to-talk, invite, sound pill) fades
  // away after FOCUS_IDLE_DELAY of no interaction, leaving just the picture and
  // the "Live" pill. It comes back on any interaction, on the phone being moved
  // (devicemotion), or when the app returns to the foreground.
  initFocusMode() {
    this.immersive = false;
    this.focusTimer = null;
    this.lastFocusArm = 0;
    this.motionAttached = false;

    const wake = () => this.wakeControls();
    // Discrete interactions: reveal controls now and restart the idle clock.
    ['pointerdown', 'keydown', 'wheel'].forEach((evt) =>
      document.addEventListener(evt, wake, { passive: true })
    );
    // Pointer movement (desktop) keeps controls up, throttled so a moving mouse
    // doesn't thrash the timer.
    document.addEventListener('pointermove', () => {
      if (this.immersive || Date.now() - this.lastFocusArm > 1000) this.wakeControls();
    }, { passive: true });
    // Screen re-activated / app foregrounded → always show controls.
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') this.wakeControls();
    });
    window.addEventListener('focus', wake);

    this.setupMotionWake();
  }

  // Wire "moving the phone brings the controls back". Android/desktop expose
  // devicemotion straight away; iOS 13+ gates it behind a permission that must be
  // requested from a user gesture, so defer that to the first tap. Either way the
  // tap/visibility paths already cover the reveal, so a denial costs nothing.
  setupMotionWake() {
    if (typeof window.DeviceMotionEvent === 'undefined') return;
    const needsPermission = typeof window.DeviceMotionEvent.requestPermission === 'function';
    if (!needsPermission) { this.attachMotionWake(); return; }
    const ask = async () => {
      try {
        const res = await window.DeviceMotionEvent.requestPermission();
        if (res === 'granted') this.attachMotionWake();
      } catch (e) { /* denied/unavailable — tap fallback still works */ }
    };
    document.addEventListener('pointerdown', ask, { once: true });
  }

  attachMotionWake() {
    if (this.motionAttached) return;
    this.motionAttached = true;
    let lastMag = null;
    window.addEventListener('devicemotion', (e) => {
      const a = (e.acceleration && e.acceleration.x != null)
        ? e.acceleration
        : e.accelerationIncludingGravity;
      if (!a) return;
      const mag = Math.hypot(a.x || 0, a.y || 0, a.z || 0);
      if (lastMag != null && Math.abs(mag - lastMag) > MOTION_WAKE_DELTA) {
        // Only act while hidden; no need to thrash the timer on every wobble.
        if (this.immersive) this.wakeControls();
      }
      lastMag = mag;
    });
  }

  // Focus mode only makes sense on a steady, live picture in the foreground:
  // never while connecting/reconnecting (the status must stay visible) or on the
  // scan screen.
  canFocus() {
    const b = document.body.classList;
    return b.contains('stage-client') && b.contains('has-stream')
      && !b.contains('reconnecting') && !b.contains('need-scan')
      && !b.contains('camera-frozen');
  }

  armFocusTimer() {
    clearTimeout(this.focusTimer);
    this.focusTimer = null;
    this.lastFocusArm = Date.now();
    if (!this.canFocus()) return;
    this.focusTimer = setTimeout(() => this.enterImmersive(), FOCUS_IDLE_DELAY);
  }

  enterImmersive() {
    if (this.immersive || !this.canFocus()) return;
    if (document.visibilityState !== 'visible') return;
    // Don't swallow the controls out from under an open dialog (e.g. Invite) or
    // while push-to-talk is being held — re-arm and try again later instead.
    if (document.querySelector('.overlay:not([hidden])')
      || ($talkBack && $talkBack.classList.contains('talking'))) {
      this.armFocusTimer();
      return;
    }
    this.immersive = true;
    document.body.classList.add('immersive');
  }

  // Reveal the controls (if hidden) and restart the idle clock.
  wakeControls() {
    if (this.immersive) {
      this.immersive = false;
      document.body.classList.remove('immersive');
    }
    this.armFocusTimer();
  }

  // ---- Audio on/off --------------------------------------------------------
  bindMute() {
    if ($muteToggle) $muteToggle.addEventListener('click', () => this.setMuted(!$video.muted));
  }
  setMuted(muted) {
    $video.muted = muted;
    if (!muted && $video.volume === 0) $video.volume = 1;
    this.syncMuteButton();
  }
  syncMuteButton() {
    setPressed($muteToggle, $video.muted);
  }

  // ---- Video framing: fit toggle + pinch-zoom + pan ------------------------
  bindFraming() {
    if ($fitToggle) $fitToggle.addEventListener('click', () => this.toggleFit());
    this.bindGestures();
    this.updateFitButton();
    this.applyFraming();
    // Re-clamp/re-fit whenever the feed's intrinsic size becomes known or the
    // viewport changes (rotation, resize, on-screen keyboard, etc.).
    if ($video) $video.addEventListener('loadedmetadata', () => this.onVideoMetadata());
    window.addEventListener('resize', () => this.applyFraming());
  }

  // Called once a (re)connected stream's intrinsic size is known. Reset the
  // zoom/pan only when the feed's orientation flipped (e.g. the host rotated the
  // phone), so a plain reconnect keeps the parent's chosen framing.
  onVideoMetadata() {
    const w = $video && $video.videoWidth;
    const h = $video && $video.videoHeight;
    if (!w || !h) { this.applyFraming(); return; }
    const orient = w > h ? 'land' : (w < h ? 'port' : 'square');
    // First time we know the real dimensions this session: restore the parent's
    // remembered fit/zoom/pan. Doing it here (not on the raw 'stream' event) means
    // applyFraming clamps the pan against the true frame size, so a saved pan is
    // preserved rather than snapped to zero while dimensions are still unknown.
    if (!this._viewRestored) {
      this._viewRestored = true;
      this.loadView();
      this.updateFitButton();
      this.lastOrient = orient;
      this.applyFraming();
      return;
    }
    if (this.lastOrient && this.lastOrient !== orient) {
      this.resetView();
    } else {
      this.applyFraming();
    }
    this.lastOrient = orient;
  }

  // Load the remembered framing for the monitor we're watching (best-effort;
  // absent/garbled storage just leaves the defaults in place). Values are clamped
  // on apply, so a pan saved under a different viewport can't push the picture
  // into a black gap.
  loadView() {
    if (!this.hostId) return;
    const v = getStoredJSON(VIEW_KEY, {})[this.hostId];
    if (!v) return;
    if (v.fitMode === 'cover' || v.fitMode === 'contain') this.fitMode = v.fitMode;
    if (typeof v.zoom === 'number' && isFinite(v.zoom)) this.zoom = this.clamp(v.zoom, 1, this.maxZoom);
    if (typeof v.tx === 'number' && isFinite(v.tx)) this.tx = v.tx;
    if (typeof v.ty === 'number' && isFinite(v.ty)) this.ty = v.ty;
  }

  // Persist the current framing for this monitor. Called at the end of a
  // deliberate change (fit toggle, double-tap, or a pan/pinch gesture), never on
  // every animation frame.
  persistView() {
    if (!this.hostId) return;
    const all = getStoredJSON(VIEW_KEY, {}) || {};
    all[this.hostId] = { fitMode: this.fitMode, zoom: this.zoom, tx: this.tx, ty: this.ty };
    setStoredJSON(VIEW_KEY, all);
  }

  toggleFit() {
    this.fitMode = this.fitMode === 'cover' ? 'contain' : 'cover';
    // Each fit mode starts from a clean, centred 1× view.
    this.resetView();
    this.updateFitButton();
    track(EVENTS.VIDEO_FIT_CHANGED, { mode: this.fitMode });
  }

  updateFitButton() {
    if (!$fitToggle) return;
    const contain = this.fitMode === 'contain';
    setPressed($fitToggle, contain);
    const label = $fitToggle.querySelector('.tbtn-label');
    if (label) label.textContent = contain ? t('toolbar.fit') : t('toolbar.fill');
  }

  clamp(v, min, max) { return Math.min(max, Math.max(min, v)); }

  // Geometry for the current stream + viewport. `contentW/H` is how large the
  // feed renders under object-fit:contain (letterboxed to fit the viewport);
  // `baseScale` is the extra factor that turns that into the active fit —
  // 1 for 'contain', or the ≥1 factor that fills both axes for 'cover'.
  frameMetrics() {
    const boxW = ($video && $video.clientWidth) || window.innerWidth;
    const boxH = ($video && $video.clientHeight) || window.innerHeight;
    const natW = ($video && $video.videoWidth) || 0;
    const natH = ($video && $video.videoHeight) || 0;
    if (!natW || !natH) {
      return { boxW, boxH, contentW: boxW, contentH: boxH, baseScale: 1 };
    }
    const containScale = Math.min(boxW / natW, boxH / natH);
    const contentW = natW * containScale;
    const contentH = natH * containScale;
    const baseScale = this.fitMode === 'cover'
      ? Math.max(boxW / contentW, boxH / contentH)
      : 1;
    return { boxW, boxH, contentW, contentH, baseScale };
  }

  // Max translation (px) on each axis that keeps the scaled picture covering the
  // viewport; 0 when the picture is smaller than the viewport there, so it stays
  // centred instead of drifting into a black gap (e.g. the letterbox axis at 1×).
  panBounds(scale, m) {
    return {
      x: Math.max(0, (m.contentW * scale - m.boxW) / 2),
      y: Math.max(0, (m.contentH * scale - m.boxH) / 2),
    };
  }

  applyFraming() {
    if (!$video) return;
    const m = this.frameMetrics();
    const scale = m.baseScale * this.zoom;
    const bounds = this.panBounds(scale, m);
    this.tx = this.clamp(this.tx, -bounds.x, bounds.x);
    this.ty = this.clamp(this.ty, -bounds.y, bounds.y);
    // Render with contain and synthesise BOTH the fill-crop and the user zoom via
    // one transform, so pan/zoom behave identically in either fit mode.
    // transform-origin is the viewport centre, so translate() is plain screen px.
    $video.style.objectFit = 'contain';
    $video.style.objectPosition = '50% 50%';
    $video.style.transformOrigin = 'center center';
    $video.style.transform = `translate(${this.tx}px, ${this.ty}px) scale(${scale})`;
    $video.classList.toggle('pannable', bounds.x > 0.5 || bounds.y > 0.5);
  }

  resetView() {
    this.zoom = 1;
    this.tx = 0;
    this.ty = 0;
    this.applyFraming();
    this.persistView();
  }

  // New translation that keeps the picture point currently under `(px, py)`
  // fixed while the scale changes from `s0` to `s1` (used by both pinch and
  // double-tap so the zoom grows out of the focal point).
  zoomAround(px, py, s0, s1, baseTx, baseTy, m) {
    const cx = m.boxW / 2;
    const cy = m.boxH / 2;
    const ratio = s0 ? s1 / s0 : 1;
    return {
      tx: px - cx - ratio * (px - cx - baseTx),
      ty: py - cy - ratio * (py - cy - baseTy),
    };
  }

  bindGestures() {
    if (!$video) return;
    const pointers = new Map();     // pointerId -> { x, y }
    let mode = null;                // 'pan' | 'pinch'
    let moved = false;              // a real drag/pinch happened (suppresses tap)
    let lastTapAt = 0;

    // Pan anchor.
    let panStartX = 0, panStartY = 0, panBaseTx = 0, panBaseTy = 0;
    // Pinch anchor (captured when the second finger lands).
    let pinchStartDist = 0, pinchBaseZoom = 1, pinchBaseTx = 0, pinchBaseTy = 0;
    let pinchMidX = 0, pinchMidY = 0;

    const midpoint = () => {
      let sx = 0, sy = 0;
      pointers.forEach((p) => { sx += p.x; sy += p.y; });
      const n = pointers.size || 1;
      return { x: sx / n, y: sy / n };
    };
    const spread = () => {
      const pts = [...pointers.values()];
      if (pts.length < 2) return 0;
      return Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
    };

    const beginPan = () => {
      mode = 'pan';
      const p = [...pointers.values()][0];
      panStartX = p.x; panStartY = p.y;
      panBaseTx = this.tx; panBaseTy = this.ty;
    };
    const beginPinch = () => {
      mode = 'pinch';
      moved = true;
      pinchStartDist = spread() || 1;
      pinchBaseZoom = this.zoom;
      pinchBaseTx = this.tx; pinchBaseTy = this.ty;
      const mid = midpoint();
      pinchMidX = mid.x; pinchMidY = mid.y;
    };

    $video.addEventListener('pointerdown', (e) => {
      pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      try { $video.setPointerCapture(e.pointerId); } catch (err) { /* ignore */ }
      $video.classList.add('panning');
      if (pointers.size === 1) { moved = false; beginPan(); }
      else if (pointers.size === 2) beginPinch();
    });

    $video.addEventListener('pointermove', (e) => {
      if (!pointers.has(e.pointerId)) return;
      pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

      if (mode === 'pinch' && pointers.size >= 2) {
        const m = this.frameMetrics();
        const dist = spread() || pinchStartDist;
        const zoom = this.clamp(pinchBaseZoom * (dist / pinchStartDist), 1, this.maxZoom);
        const s0 = m.baseScale * pinchBaseZoom;
        const s1 = m.baseScale * zoom;
        // Anchor the zoom to the pinch's starting midpoint, then also follow the
        // midpoint as the fingers slide together (two-finger pan).
        const mid = midpoint();
        const z = this.zoomAround(pinchMidX, pinchMidY, s0, s1, pinchBaseTx, pinchBaseTy, m);
        this.zoom = zoom;
        this.tx = z.tx + (mid.x - pinchMidX);
        this.ty = z.ty + (mid.y - pinchMidY);
        this.applyFraming();
        return;
      }

      if (mode === 'pan' && pointers.size === 1) {
        const p = pointers.get(e.pointerId);
        const dx = p.x - panStartX;
        const dy = p.y - panStartY;
        if (Math.abs(dx) > 3 || Math.abs(dy) > 3) moved = true;
        this.tx = panBaseTx + dx;
        this.ty = panBaseTy + dy;
        this.applyFraming();
      }
    });

    const removePointer = (e) => {
      if (!pointers.has(e.pointerId)) return;
      pointers.delete(e.pointerId);
      try { $video.releasePointerCapture(e.pointerId); } catch (err) { /* ignore */ }
      if (pointers.size === 1) {
        // Lifting one finger of a pinch: carry on as a pan from the remaining one.
        beginPan();
      } else if (pointers.size === 0) {
        $video.classList.remove('panning');
        const wasGesture = moved;
        mode = null;
        // Remember the framing after a real pan/pinch (a double-tap persists via
        // its own reset/zoom path below).
        if (wasGesture) this.persistView();
        // A quick double-tap (no drag/pinch) zooms in at the tapped point, or
        // snaps back to a centred 1× view when already zoomed.
        if (!wasGesture) {
          const now = Date.now();
          if (now - lastTapAt < 300) {
            lastTapAt = 0;
            this.handleDoubleTap(e.clientX, e.clientY);
          } else {
            lastTapAt = now;
          }
        }
      }
    };
    $video.addEventListener('pointerup', removePointer);
    $video.addEventListener('pointercancel', removePointer);
  }

  handleDoubleTap(px, py) {
    // Already zoomed OR just panned (cover mode is pannable at 1×): snap back.
    if (this.zoom > 1.01 || Math.abs(this.tx) > 1 || Math.abs(this.ty) > 1) {
      this.resetView();
      return;
    }
    const m = this.frameMetrics();
    const target = Math.min(2.5, this.maxZoom);
    const z = this.zoomAround(px, py, m.baseScale * this.zoom, m.baseScale * target, this.tx, this.ty, m);
    this.zoom = target;
    this.tx = z.tx;
    this.ty = z.ty;
    this.applyFraming();
    this.persistView();
  }

  // ---- Push-to-talk --------------------------------------------------------
  setMicEnabled(enabled) {
    if (!this.audioStream) return;
    this.audioStream.getAudioTracks().forEach((track) => { track.enabled = enabled; });
    $talkBack.classList.toggle('talking', enabled);
  }
  bindTalkBack() {
    const start = (e) => {
      e.preventDefault();
      if (e.pointerId != null && $talkBack.setPointerCapture) {
        try { $talkBack.setPointerCapture(e.pointerId); } catch (err) { /* ignore */ }
      }
      this.setMicEnabled(true);
      track(EVENTS.TALKBACK_USED);
    };
    const end = () => this.setMicEnabled(false);
    $talkBack.addEventListener('pointerdown', start);
    $talkBack.addEventListener('pointerup', end);
    $talkBack.addEventListener('pointercancel', end);
    $talkBack.addEventListener('contextmenu', (e) => e.preventDefault());
  }

  // ---- Background alerts (Web Push) ---------------------------------------
  bindAlerts() {
    if (!$alertsToggle) return;
    let on = !!getStoredJSON(ALERT_KEY, {}).enabled;

    const reflect = () => setPressed($alertsToggle, on);
    const persist = () => setStoredJSON(ALERT_KEY, { enabled: on });

    // Silently re-subscribe if the user had alerts on and already granted
    // notification permission (host detection drives the actual alerts).
    if (on && 'Notification' in window && Notification.permission === 'granted' && this.hostId) {
      this._subscribedHostId = this.hostId;
      enablePush(this.hostId).catch(() => {});
    }
    reflect();

    let busy = false;
    $alertsToggle.addEventListener('click', async () => {
      // Ignore taps while a previous toggle is still resolving (the enable path
      // is async — permission prompt + network — so rapid taps would otherwise
      // race enable/disable and leave the button, storage and server disagreeing).
      if (busy) return;
      busy = true;
      try {
        if (on) {
          on = false;
          reflect();
          persist();
          this._subscribedHostId = null;
          disablePush().catch(() => {});
          this.toast(t('alerts.disabled'));
          return;
        }

        // Optimistically show the pressed state, then confirm (or roll back)
        // once we know whether push could actually be enabled.
        on = true;
        reflect();
        const result = await enablePush(this.hostId).catch(() => 'error');
        if (result === 'ok') {
          persist();
          this._subscribedHostId = this.hostId;
          this.toast(t('alerts.enabled'));
          return;
        }

        on = false;
        reflect();
        persist();
        if (result === 'unsupported') {
          // On iOS, Web Push only works once the app is on the Home Screen, so
          // point the parent there (with the Share glyph so the action is
          // recognisable) rather than saying it's flat-out unsupported.
          if (isIos()) {
            this.toast(t('alerts.unsupportedIos'), { icon: 'ios' });
          } else {
            this.toast(t('alerts.unsupported'));
          }
        } else if (result === 'denied') {
          this.toast(t('alerts.denied'));
        } else {
          this.toast(t('alerts.failed'));
        }
      } finally {
        busy = false;
      }
    });
  }

  // Ensure the push subscription is registered under the host we're actually
  // watching. Called on every (re)connect; the hostId guard makes it a no-op
  // unless alerts are on, permission is granted, and the host id has changed —
  // so it costs nothing on a normal reconnect but fixes the scan flow (hostId
  // set after init) and a host that reclaimed/rotated its id.
  maybeRefreshPush() {
    if (!this.hostId) return;
    if (!('Notification' in window) || Notification.permission !== 'granted') return;
    if (!getStoredJSON(ALERT_KEY, {}).enabled) return;
    if (this._subscribedHostId === this.hostId) return;
    this._subscribedHostId = this.hostId;
    enablePush(this.hostId).catch(() => {});
  }

  // Transient, non-blocking message for the parent (e.g. why alerts couldn't be
  // turned on). Auto-dismisses; a new message replaces any visible one. Pass
  // `{ icon: 'ios' | 'android' }` for instructional hints to prepend a platform
  // glyph; those stay up a little longer since there's an action to follow.
  toast(text, opts = {}) {
    const el = document.getElementById('toast');
    if (!el) return;
    const iconMarkup = opts.icon ? HINT_ICONS[opts.icon] : null;
    el.classList.toggle('toast--hint', !!iconMarkup);
    if (iconMarkup) {
      el.textContent = '';
      el.insertAdjacentHTML('beforeend', iconMarkup);
      const span = document.createElement('span');
      span.className = 'toast-text';
      span.textContent = text;
      el.appendChild(span);
    } else {
      el.textContent = text;
    }
    openToast(el);
    clearTimeout(this._toastTimer);
    this._toastTimer = setTimeout(() => closeToast(el), iconMarkup ? 6000 : 4200);
  }

  // ---- Invite / other parents ---------------------------------------------
  bindInvite() {
    if ($invite) {
      $invite.addEventListener('click', () => {
        if (!$inviteOverlay) return;
        this.renderInviteQR();
        $inviteOverlay.hidden = false;
      });
    }
    if ($inviteOverlay) {
      $inviteOverlay.addEventListener('click', (e) => {
        if (e.target === $inviteOverlay || e.target.closest('.overlay-close')) $inviteOverlay.hidden = true;
      });
    }
    if ($inviteCopy) $inviteCopy.addEventListener('click', () => this.copyLink());
  }

  inviteLink() {
    return hostLink(this.hostId);
  }

  renderInviteQR() {
    renderQR($inviteOverlay && $inviteOverlay.querySelector('.invite-qr'), this.inviteLink());
  }

  async copyLink() {
    const label = $inviteCopy && $inviteCopy.querySelector('.copy-label');
    if (await copyWithFeedback(this.inviteLink(), label)) track(EVENTS.LINK_COPIED);
  }

  // ---- Lullaby (shared control with the baby monitor) ----------------------
  // The lullaby plays out of the baby monitor's speaker so it reaches the baby;
  // the parent only hears it through the live stream. Here we just let the
  // parent start/stop it remotely and reflect the state the host reports back.
  bindMelody() {
    if (!$melodyToggle) return;
    $melodyToggle.addEventListener('click', () => {
      const next = !this.melodyOn;
      // Only act if the command actually goes out. Otherwise (data channel not
      // open yet) the tap is a no-op and the button stays honest — the host's
      // state applies as soon as we're connected.
      if (!this.sendMelodyCommand(next)) return;
      // Optimistic: light the button now; the host echoes the authoritative
      // state back and corrects us if playback couldn't start there.
      this.setMelodyState(next);
      track(EVENTS.MELODY_TOGGLED, { playing: next, source: 'parent' });
    });
  }

  sendMelodyCommand(on) {
    try {
      if (this.dc && this.dc.open) {
        this.dc.send({ type: 'melody', playing: on });
        return true;
      }
    } catch (e) {
      /* the button resyncs from the host's next broadcast */
    }
    return false;
  }

  setMelodyState(on) {
    this.melodyOn = !!on;
    setPressed($melodyToggle, this.melodyOn);
    // Subtle top-right badge so the parent can tell the lullaby is playing.
    document.body.classList.toggle('melody-playing', this.melodyOn);
  }

  // Keep a metadata channel open for as long as we have a media call. The channel
  // is on its own peer connection, so it can fail on its own (or never come back
  // after a reconnect that reused the media path); a light poll re-establishes it.
  // Idempotent, so the poll, a fresh 'stream', a foreground resume and an ICE
  // restart can all call ensureDataChannel() without stacking channels.
  startDataChannelSupervisor() {
    if (this.dcSupervisor) return;
    this.dcSupervisor = setInterval(() => this.ensureDataChannel(), DC_SUPERVISOR_MS);
  }

  stopDataChannelSupervisor() {
    if (this.dcSupervisor) clearInterval(this.dcSupervisor);
    this.dcSupervisor = null;
  }

  // Only bother while a media call is in place: with no call there's nothing to
  // annotate, and the reconnect loop will open a fresh channel once the feed is
  // back. Retained name `openDataChannel` for the 'stream' call site.
  ensureDataChannel() {
    if (this.dc || !this.hostId || !this.currentCall) return;
    // Don't open while signaling is down: peer.connect() would hand back a
    // channel that can't complete its offer and may never fire close, wedging
    // this.dc non-null so the supervisor never retries. Wait for reconnect.
    if (!this.peer || this.peer.disconnected || this.peer.destroyed) return;
    try {
      const dc = this.peer.connect(this.hostId, { reliable: true });
      this.dc = dc;
      dc.on('data', (msg) => {
        if (!msg) return;
        if (msg.type === 'count') this.renderOthers(msg.count);
        else if (msg.type === 'melody') this.setMelodyState(!!msg.playing);
        else if (msg.type === 'battery') this.setBatteryState(msg);
        else if (msg.type === 'live') this.noteHostFrame(msg.seq);
      });
      // Null out on teardown so the supervisor reopens; guard that a late event
      // from a superseded channel can't clear a newer one.
      dc.on('close', () => { if (this.dc === dc) this.dc = null; });
      dc.on('error', () => { if (this.dc === dc) this.dc = null; });
    } catch (e) {
      /* best-effort — the supervisor retries on the next tick */
    }
  }

  openDataChannel() {
    this.ensureDataChannel();
  }

  renderOthers(total) {
    const others = Math.max(0, (total || 0) - 1);
    if (!$othersBadge) return;
    $othersBadge.hidden = others === 0;
    $othersBadge.textContent = String(others);
  }

  // ---- Monitor battery warning --------------------------------------------
  // Driven entirely by the host's data-channel messages. On the rising edge we
  // sound a soft tone and start repeating it every 5 minutes; while low we keep
  // the percentage fresh; on recovery/charging we clear everything.
  setBatteryState({ low, level }) {
    document.body.classList.toggle('battery-low', !!low);
    if ($batteryIndicator && level != null) {
      const $level = $batteryIndicator.querySelector('.battery-level');
      if ($level) $level.textContent = t('battery.level', { level });
    }
    if (low && !this.batteryLow) {
      // Rising edge: nudge the parent now, then keep nudging every 5 minutes.
      playBatteryWarning();
      clearInterval(this.batteryTimer);
      this.batteryTimer = setInterval(() => playBatteryWarning(), 5 * 60 * 1000);
    } else if (!low && this.batteryLow) {
      this.clearBatteryWarning();
    }
    this.batteryLow = !!low;
  }

  // Silence the repeating tone and drop the pill. Called on recovery and also
  // when the link drops (see teardownCall): a dying monitor is a likely cause of
  // the drop, but the connection-lost cue + reconnecting overlay already own
  // that moment — a lingering battery beep would only stack on top, and its real
  // cause (battery vs. plain network drop) is ambiguous once the link is gone.
  // The host re-sends battery state on the next data channel, so a still-low
  // battery re-arms the warning as soon as we're back.
  clearBatteryWarning() {
    clearInterval(this.batteryTimer);
    this.batteryTimer = null;
    this.batteryLow = false;
    document.body.classList.remove('battery-low');
  }

  // ---- Passive sound indicator --------------------------------------------
  onSoundLevel(level) {
    if (level >= SOUND_ON) {
      this.soundPeak = Math.max(this.soundPeak, level);
      this.lastSound = { at: Date.now(), band: this.bandFor(this.soundPeak) };
    }
    // Decay the peak so a brief loud spike doesn't stick forever.
    this.soundPeak *= 0.9;
  }

  bandFor(level) {
    if (level >= 0.55) return 'hard';
    if (level >= 0.3) return 'medium';
    return 'low';
  }

  startSoundClock() {
    this.soundTimer = setInterval(() => this.renderSound(), 1000);
    this.renderSound();
  }

  // Compact meter: the icon's colour + waves carry the live level, and the tiny
  // sub-line shows only the elapsed time once sound has gone quiet (a lit icon is
  // the "now" signal, so no text is needed while sound is present). The full,
  // wordy state still goes to aria-label for screen readers.
  renderSound() {
    if (!$soundIndicator) return;
    const $ago = $soundIndicator.querySelector('.sound-ago');
    $soundIndicator.classList.remove('lvl-low', 'lvl-medium', 'lvl-hard');

    if (!this.lastSound) {
      if ($ago) $ago.textContent = '';
      $soundIndicator.setAttribute('aria-label', `${t('sound.quiet')} · ${t('sound.none')}`);
      return;
    }

    const secs = Math.round((Date.now() - this.lastSound.at) / 1000);
    const recent = secs <= 2;
    const band = this.lastSound.band;
    // Internal bands map to the i18n level keys (the loudest is "loud").
    const bandKey = { low: 'low', medium: 'medium', hard: 'loud' }[band] || 'low';

    if (recent) {
      $soundIndicator.classList.add(`lvl-${band}`);
      if ($ago) $ago.textContent = '';
      $soundIndicator.setAttribute('aria-label', t(`sound.level.${bandKey}`));
    } else {
      if ($ago) $ago.textContent = this.timeAgo(secs);
      $soundIndicator.setAttribute(
        'aria-label',
        `${t(`sound.past.${bandKey}`)} · ${t('sound.ago', { time: this.timeAgo(secs) })}`
      );
    }
  }

  timeAgo(secs) {
    if (secs < 60) return `${secs}s`;
    const mins = Math.floor(secs / 60);
    if (mins < 60) return `${mins}m`;
    return `${Math.floor(mins / 60)}h`;
  }
}
